import { $, gsap } from "../shared/globals.js";
import { getSceneImageClass } from "../utils/getSceneImageClass.js";

export function createGridScrollFeature({ context, prefersReducedMotion, CONFIG }) {
    const {
        $gridScrollScene,
        $gridTopTextPath,
        $gridBottomTextPath,
        $gridScrollTiger,
        $gridScrollTigerStatic,
        $gridScrollTigerFrames,
        $sectionFloatingImgs,
        gridImageClasses,
    } = context;

    let gridScrollTextTrigger = null;
    let gridScrollImagesTrigger = null;
    let gridScrollTigerTrigger = null;
    let gridFallbackId = null;
    let gridTigerWalkInterval = null;
    let currentGridTigerFrame = 1;

    function showGridTigerStatic() {
        if (!$gridScrollTiger.length) return;

        $gridScrollTigerFrames.removeClass("active");
        $gridScrollTigerStatic.addClass("active");
    }

    function showGridTigerFrame(frameNumber) {
        if (!$gridScrollTiger.length) return;

        $gridScrollTigerStatic.removeClass("active");
        $gridScrollTigerFrames.removeClass("active");
        $gridScrollTiger.find(`.event-tiger-frame-${frameNumber}`).addClass("active");
    }

    function getGridTextOffsets() {
        const isExtraSmallMobile = window.matchMedia("(max-width: 479px)").matches;
        const isLargeGridScene = $gridScrollScene.length && !$gridScrollScene.hasClass("is-smaller-grid");

        if (isExtraSmallMobile && isLargeGridScene) {
            return {
                topStart: "30%",
                topEnd: "-105%",
                bottomStart: "50%",
                bottomEnd: "6%",
            };
        }

        return {
            topStart: "0%",
            topEnd: "10%",
            bottomStart: "50%",
            bottomEnd: "25%",
        };
    }

    function startGridTigerWalking() {
        const totalFrames = $gridScrollTigerFrames.length;

        if (!$gridScrollTiger.length || !totalFrames || gridTigerWalkInterval) return;

        currentGridTigerFrame = 1;
        showGridTigerFrame(currentGridTigerFrame);

        gridTigerWalkInterval = window.setInterval(() => {
            currentGridTigerFrame += 1;

            if (currentGridTigerFrame > totalFrames) {
                currentGridTigerFrame = 1;
            }

            showGridTigerFrame(currentGridTigerFrame);
        }, CONFIG.tigerWalkFrameRate);
    }

    function stopGridTigerWalking() {
        if (gridTigerWalkInterval) {
            window.clearInterval(gridTigerWalkInterval);
            gridTigerWalkInterval = null;
        }

        currentGridTigerFrame = 1;
        showGridTigerStatic();
    }

    function initGridScrollTextAnimation() {
        if (!$gridScrollScene.length || (!$gridTopTextPath.length && !$gridBottomTextPath.length)) return;

        if (gridScrollTextTrigger) {
            gridScrollTextTrigger.kill();
            gridScrollTextTrigger = null;
        }

        if (prefersReducedMotion) {
            const offsets = getGridTextOffsets();

            if ($gridTopTextPath.length) {
                gsap.set($gridTopTextPath, { attr: { startOffset: offsets.topEnd } });
            }

            if ($gridBottomTextPath.length) {
                gsap.set($gridBottomTextPath, { attr: { startOffset: offsets.bottomEnd } });
            }

            return;
        }

        const offsets = getGridTextOffsets();
        const gridScrollTimeline = gsap.timeline({
            defaults: {
                ease: "none",
            },
            scrollTrigger: {
                trigger: $gridScrollScene[0],
                start: "top 85%",
                end: "bottom 15%",
                scrub: 1.2,
                invalidateOnRefresh: true,
            },
        });

        if ($gridTopTextPath.length) {
            gsap.set($gridTopTextPath, { attr: { startOffset: offsets.topStart } });
            gridScrollTimeline.to($gridTopTextPath, { attr: { startOffset: offsets.topEnd } }, 0);
        }

        if ($gridBottomTextPath.length) {
            gsap.set($gridBottomTextPath, { attr: { startOffset: offsets.bottomStart } });
            gridScrollTimeline.to($gridBottomTextPath, { attr: { startOffset: offsets.bottomEnd } }, 0);
        }

        gridScrollTextTrigger = gridScrollTimeline.scrollTrigger;
    }

    function initGridScrollImagesAnimation() {
        if (!$gridScrollScene.length || !$sectionFloatingImgs.length) return;

        if (gridScrollImagesTrigger) {
            gridScrollImagesTrigger.kill();
            gridScrollImagesTrigger = null;
        }

        if (prefersReducedMotion) {
            gsap.set($sectionFloatingImgs, { x: 0, y: 0 });
            return;
        }

        const imageOffsets = {
            "img-f-01": { x: -30, y: -54 },
            "img-f-02": { x: 38, y: -24 },
            "img-f-03": { x: -22, y: 42 },
            "img-f-04": { x: 32, y: 28 },
            "img-s-01": { x: -30, y: -54 },
            "img-s-02": { x: 38, y: -24 },
            "img-s-03": { x: -22, y: 42 },
            "img-s-04": { x: 32, y: 28 },
        };

        const gridImagesTimeline = gsap.timeline({
            defaults: {
                ease: "none",
            },
            scrollTrigger: {
                trigger: $gridScrollScene[0],
                start: "top 90%",
                end: "bottom 10%",
                scrub: 1.1,
                invalidateOnRefresh: true,
            },
        });

        $sectionFloatingImgs.each(function(index) {
            const $img = $(this);
            const imgClass = getSceneImageClass($img, gridImageClasses);
            const config = imageOffsets[imgClass] || {
                x: index % 2 === 0 ? 20 : -20,
                y: index % 2 === 0 ? -30 : 30,
            };

            gsap.set(this, { x: 0, y: 0 });
            gridImagesTimeline.to(this, { x: config.x, y: config.y }, 0);
        });

        gridScrollImagesTrigger = gridImagesTimeline.scrollTrigger;
    }

    function initGridScrollTigerAnimation() {
        if (!$gridScrollScene.length || !$gridScrollTiger.length) return;

        if (gridScrollTigerTrigger) {
            gridScrollTigerTrigger.kill();
            gridScrollTigerTrigger = null;
        }

        stopGridTigerWalking();

        if (prefersReducedMotion) {
            gsap.set($gridScrollTiger, { x: 0, y: 0, rotation: 0 });
            showGridTigerStatic();
            return;
        }

        gridScrollTigerTrigger = gsap.timeline({
            defaults: {
                ease: "none",
            },
            scrollTrigger: {
                trigger: $gridScrollScene[0],
                start: "top 90%",
                end: "bottom 10%",
                scrub: 1,
                invalidateOnRefresh: true,
                onEnter: () => {
                    startGridTigerWalking();
                },
                onEnterBack: () => {
                    startGridTigerWalking();
                },
                onLeave: () => {
                    stopGridTigerWalking();
                },
                onLeaveBack: () => {
                    stopGridTigerWalking();
                },
            },
        }).to($gridScrollTiger[0], {
            x: 18,
            y: -16,
            rotation: -2,
        }, 0);

        if (gridScrollTigerTrigger.scrollTrigger?.isActive) {
            startGridTigerWalking();
        }
    }

    function init() {
        initGridScrollTextAnimation();
        initGridScrollImagesAnimation();
        initGridScrollTigerAnimation();
    }

    function initOnLoad() {
        $(window).on("load.grid-scroll-text", init);

        gridFallbackId = window.setTimeout(() => {
            init();
        }, 500);
    }

    function destroy() {
        if (gridFallbackId) {
            window.clearTimeout(gridFallbackId);
            gridFallbackId = null;
        }

        $(window).off("load.grid-scroll-text", init);

        if (gridScrollTextTrigger) {
            gridScrollTextTrigger.kill();
            gridScrollTextTrigger = null;
        }

        if (gridScrollImagesTrigger) {
            gridScrollImagesTrigger.kill();
            gridScrollImagesTrigger = null;
        }

        if (gridScrollTigerTrigger) {
            gridScrollTigerTrigger.kill();
            gridScrollTigerTrigger = null;
        }

        if ($gridScrollTiger.length) {
            gsap.set($gridScrollTiger, { clearProps: "x,y,rotation" });
        }

        stopGridTigerWalking();
    }

    return {
        initOnLoad,
        destroy,
    };
}
